import { createStyles, Divider, Group, Modal, Stack } from '@mantine/core';

import { Button } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import type { IApiListResponse, ISecodaEntity } from '../../../api';
import type { ITag } from '../../../api/types/models/tag';
import type { AiOutputBlockStore } from '../store';
import { AiSettingModalContent } from './AiSettingModalContent';
import { AiSettingModalLeftColumnContent } from './AiSettingModalLeftColumnContent';
import { AiSettingModalRightColumnContent } from './AiSettingModalRightColumnContent';

const useStyles = createStyles((theme) => ({
	content: {
		height: '95%',
	},
	header: {
		position: 'initial',
	},
	body: {
		height: 'calc(100% - 54px)', // 54px is the height of the modal header
		padding: `${0} !important`,
	},
	stack: {
		height: '100%',
		gap: 0,
	},
	group: {
		gap: theme.spacing.xs,
		padding: theme.spacing.md,
	},
	divider: {
		margin: 0,
		width: '100%',
	},
	text: {
		paddingLeft: theme.spacing.md,
		paddingRight: theme.spacing.md,
	},
}));

export interface AddAiSettingModalProps {
	store: AiOutputBlockStore;
	onComplete: () => void;
	onClose: () => void;
	resources: IApiListResponse<ISecodaEntity> | undefined;
	isReview: boolean;
	onReviewClose: () => void;
	onPreview: () => void;
	setPreviewIndex: (index: number) => void;
	onFeedbackSubmit: (sentiment: 'positive' | 'negative') => Promise<void>;
	isSubmittingFeedback: boolean;
	tagsData: IApiListResponse<ITag> | undefined;
}

export const AddAiSettingModal = observer(
	({
		store,
		onComplete,
		onClose,
		resources,
		isReview,
		onReviewClose,
		onPreview,
		setPreviewIndex,
		onFeedbackSubmit,
		isSubmittingFeedback,
		tagsData,
	}: AddAiSettingModalProps) => {
		const { classes } = useStyles();

		const aiSettingStore = store.getCurrentAiSettingStore();

		const selectedResources = useMemo(
			() =>
				resources?.results.filter((resource) =>
					aiSettingStore?.aiSettingAction.resources_to_preview?.includes(
						resource.id
					)
				),
			[resources, aiSettingStore?.aiSettingAction.resources_to_preview]
		);

		const handleComplete = () => {
			aiSettingStore?.resetErrors();
			if (aiSettingStore?.checkIsSubmitReady()) {
				onComplete();
			}
		};

		const handleCancel = () => {
			if (isReview) {
				onReviewClose();
			} else {
				onClose();
			}
		};

		return (
			<Modal
				opened
				onClose={handleCancel}
				size="auto"
				classNames={classes}
				title="Create AI output"
				centered
			>
				<Stack className={classes.stack}>
					<AiSettingModalContent
						leftColumn={
							<AiSettingModalLeftColumnContent
								store={store}
								aiSettingStore={aiSettingStore || undefined}
								onPreviewOnClick={onPreview}
								resources={resources}
							/>
						}
						rightColumn={
							<AiSettingModalRightColumnContent
								store={store}
								resourceCount={selectedResources?.length ?? 0}
								setPreviewIndex={setPreviewIndex}
								onFeedbackSubmit={onFeedbackSubmit}
								isSubmittingFeedback={isSubmittingFeedback}
								tagsData={tagsData}
							/>
						}
					/>
					<Divider className={classes.divider} />
					<Group spacing="sm" position="right" className={classes.group}>
						<Button variant="default" onClick={handleCancel}>
							Cancel
						</Button>
						{!isReview && (
							<Button variant="primary" onClick={handleComplete}>
								Create AI output
							</Button>
						)}
						{isReview && (
							<Button variant="primary" onClick={handleComplete}>
								Save AI output
							</Button>
						)}
					</Group>
				</Stack>
			</Modal>
		);
	}
);
